import React, { useEffect, useMemo, useState } from "react";
import { Search, Users, Phone, Mail, Wallet, DollarSign, Loader2, X, FileText } from "lucide-react";
import * as api from "../lib/api";
import { CATEGORIES } from "../lib/constants";

const fmtEur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export default function ClientsView({ openTaskModal }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sort, setSort] = useState("credits");

  useEffect(() => {
    api.listClients().then(setClients).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = [...clients];
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(s) ||
          (c.phone || "").includes(s) ||
          (c.email || "").toLowerCase().includes(s)
      );
    }
    if (sort === "credits") list.sort((a, b) => b.totalCredits - a.totalCredits);
    else if (sort === "acconti") list.sort((a, b) => b.totalAcconti - a.totalAcconti);
    else if (sort === "tasks") list.sort((a, b) => b.totalTasks - a.totalTasks);
    else list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [clients, search, sort]);

  const openDetail = async (name) => {
    setSelected(name);
    setDetail(null);
    try {
      const d = await api.clientDetail(name);
      setDetail(d);
    } catch (e) {
      /* handled */
    }
  };

  const closeDetail = () => { setSelected(null); setDetail(null); };

  const totalCredits = useMemo(() => filtered.reduce((a, c) => a + c.totalCredits, 0), [filtered]);
  const totalAcconti = useMemo(() => filtered.reduce((a, c) => a + c.totalAcconti, 0), [filtered]);

  return (
    <div className="flex-1 bg-slate-50 p-6 overflow-y-auto custom-scrollbar" data-testid="clients-view">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-slate-900 tracking-tight flex items-center gap-3">
              <Users size={30} strokeWidth={1.75} className="text-blue-700" />
              Rubrica Clienti
            </h1>
            <p className="text-slate-500 text-sm mt-1">Storico completo per ogni cliente, ricavato dalle attività.</p>
          </div>
        </div>

        {/* Summary bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SumCard label="Clienti totali" value={clients.length} color="blue" />
          <SumCard label="Crediti totali" value={fmtEur(totalCredits)} color="emerald" valueClass="text-emerald-600" />
          <SumCard label="Acconti totali" value={fmtEur(totalAcconti)} color="cyan" valueClass="text-cyan-600" />
          <SumCard label="Filtrati" value={filtered.length} color="slate" />
        </div>

        {/* Search & sort */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 mb-6 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, telefono, email..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-sm"
              data-testid="clients-search-input"
            />
          </div>
          <select
            value={sort} onChange={(e) => setSort(e.target.value)}
            className="p-2 border border-slate-200 rounded-lg bg-white text-sm font-medium text-slate-700"
            data-testid="clients-sort-select"
          >
            <option value="credits">Ordina: Crediti ↓</option>
            <option value="acconti">Ordina: Acconti ↓</option>
            <option value="tasks">Ordina: N° Attività ↓</option>
            <option value="name">Ordina: Nome A-Z</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="animate-spin inline mr-2" /> Caricamento...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.length === 0 && (
              <div className="col-span-full text-center py-16 text-slate-400 bg-white rounded-xl border border-dashed border-slate-200">
                Nessun cliente trovato
              </div>
            )}
            {filtered.map((c) => (
              <div
                key={c.name}
                onClick={() => openDetail(c.name)}
                className="task-row bg-white p-5 rounded-xl border border-slate-200 cursor-pointer"
                data-testid={`client-card-${c.name}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-outfit font-bold text-lg text-slate-900 leading-tight truncate flex-1">{c.name}</h3>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full ml-2 shrink-0">
                    {c.openTasks}/{c.totalTasks} aperti
                  </span>
                </div>

                {(c.phone || c.email) && (
                  <div className="space-y-1 text-xs text-slate-600 mb-3">
                    {c.phone && (
                      <div className="flex items-center gap-1.5"><Phone size={12} /> <span className="font-mono">{c.phone}</span></div>
                    )}
                    {c.email && (
                      <div className="flex items-center gap-1.5"><Mail size={12} /> <span className="truncate">{c.email}</span></div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100">
                  <div>
                    <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Crediti</div>
                    <div className="font-outfit font-bold text-emerald-700">{fmtEur(c.totalCredits)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-cyan-600 uppercase tracking-wider">Acconti</div>
                    <div className="font-outfit font-bold text-cyan-700">{fmtEur(c.totalAcconti)}</div>
                  </div>
                </div>

                {c.categories?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {c.categories.slice(0, 4).map((cat) => (
                      <span key={cat} className={`text-[10px] px-2 py-0.5 rounded ${CATEGORIES[cat]?.color || "bg-slate-100 text-slate-600"}`}>
                        {CATEGORIES[cat]?.label || cat}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <ClientDetailModal
          name={selected}
          detail={detail}
          onClose={closeDetail}
          openTaskModal={(task) => { closeDetail(); openTaskModal(task); }}
        />
      )}
    </div>
  );
}

function SumCard({ label, value, valueClass = "text-slate-900" }) {
  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200">
      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-outfit font-bold ${valueClass} mt-1`}>{value}</div>
    </div>
  );
}

function ClientDetailModal({ name, detail, onClose, openTaskModal }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 modal-backdrop" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto custom-scrollbar fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-outfit font-bold text-slate-900" data-testid="client-detail-name">{name}</h2>
            {detail && (
              <div className="flex gap-4 mt-2 text-sm">
                {detail.tasks[0]?.phone && (
                  <span className="flex items-center gap-1 text-slate-600"><Phone size={13} /> <span className="font-mono">{detail.tasks[0].phone}</span></span>
                )}
                {detail.tasks[0]?.email && (
                  <a href={`mailto:${detail.tasks[0].email}`} className="flex items-center gap-1 text-blue-700 hover:underline">
                    <Mail size={13} /> {detail.tasks[0].email}
                  </a>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-client-detail">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {!detail ? (
            <div className="text-center py-10"><Loader2 className="animate-spin inline mr-2" /> Caricamento storico...</div>
          ) : (
            <>
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
                Storico attività ({detail.tasks.length})
              </h3>
              <div className="space-y-2">
                {detail.tasks.length === 0 && (
                  <div className="text-center text-slate-400 py-8">Nessuna attività</div>
                )}
                {detail.tasks.map((t) => (
                  <div
                    key={t.id}
                    onClick={() => openTaskModal(t)}
                    className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition ${
                      t.completed ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200"
                    }`}
                    data-testid={`client-task-${t.id}`}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] px-2 py-0.5 rounded ${CATEGORIES[t.category]?.color || "bg-slate-100"}`}>
                            {CATEGORIES[t.category]?.label || t.category}
                          </span>
                          {t.completed && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Completato</span>}
                        </div>
                        <p className={`text-sm text-slate-700 ${t.completed ? "line-through" : ""}`}>{t.description || "(no descrizione)"}</p>
                        {(t.invoiceNumber || t.offerNumber || t.orderNumber) && (
                          <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                            <FileText size={11} />
                            {t.invoiceNumber && <span>Fatt. {t.invoiceNumber}</span>}
                            {t.offerNumber && <span>Prev. {t.offerNumber}</span>}
                            {t.orderNumber && <span>Ord. {t.orderNumber}</span>}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        {t.dueDate && (
                          <div className="text-xs text-slate-500">{new Date(t.dueDate).toLocaleDateString("it-IT")}</div>
                        )}
                        {(t.category === "CREDITI" || t.category === "ACCONTI") && t.amount > 0 && (
                          <div className={`font-outfit font-bold text-sm mt-1 ${t.category === "CREDITI" ? "text-emerald-700" : "text-cyan-700"}`}>
                            {fmtEur(Number(t.amount) - Number(t.paidAmount || 0))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
